import { useState } from 'react';
import { Field, TextInput } from './Field';
import Icon from './Icon';

/** Mirrors the server's policy, so the rules are visible before submitting. */
export const PASSWORD_RULES = {
  required: 'Choose a password',
  minLength: { value: 10, message: 'Use at least 10 characters' },
  validate: {
    lower: (v) => /[a-z]/.test(v) || 'Include a lowercase letter',
    upper: (v) => /[A-Z]/.test(v) || 'Include an uppercase letter',
    digit: (v) => /[0-9]/.test(v) || 'Include a number',
  },
};

function strengthOf(password) {
  if (!password) return { score: 0, label: '', color: 'var(--border)' };

  let score = 0;
  if (password.length >= 10) score += 1;
  if (password.length >= 14) score += 1;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;

  const levels = [
    { label: 'Too weak', color: 'var(--danger-fg)' },
    { label: 'Weak', color: 'var(--danger-fg)' },
    { label: 'Fair', color: 'var(--warning-fg)' },
    { label: 'Good', color: 'var(--info-fg)' },
    { label: 'Strong', color: 'var(--success-fg)' },
    { label: 'Very strong', color: 'var(--success-fg)' },
  ];

  return { score, ...levels[score] };
}

/**
 * New-password + confirmation, with a live strength meter. Used by the reset,
 * invite and change-password flows so the rules stay identical everywhere.
 */
export default function PasswordFields({ register, errors, watch, names = {} }) {
  const passwordName = names.password || 'password';
  const confirmName = names.confirm || 'confirmPassword';

  const [show, setShow] = useState(false);
  const password = watch(passwordName) || '';
  const strength = strengthOf(password);

  return (
    <>
      <Field label="New password" error={errors[passwordName]?.message} required>
        <div style={{ position: 'relative' }}>
          <TextInput
            type={show ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="At least 10 characters"
            error={errors[passwordName]}
            style={{ paddingRight: 44 }}
            {...register(passwordName, PASSWORD_RULES)}
          />
          <button
            type="button"
            onClick={() => setShow((v) => !v)}
            aria-label={show ? 'Hide password' : 'Show password'}
            style={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 0,
              color: 'var(--ink-muted)',
              cursor: 'pointer',
              padding: 4,
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <Icon name={show ? 'visibility_off' : 'visibility'} style={{ fontSize: 19 }} />
          </button>
        </div>

        {password && (
          <div style={{ marginTop: 8 }}>
            <div className="bar-track" style={{ height: 5 }}>
              <div
                className="bar-fill"
                style={{ width: `${(strength.score / 5) * 100}%`, background: strength.color }}
              />
            </div>
            <div style={{ fontSize: 11, color: strength.color, fontWeight: 700, marginTop: 5 }}>
              {strength.label}
            </div>
          </div>
        )}
      </Field>

      <Field label="Confirm password" error={errors[confirmName]?.message} required>
        <TextInput
          type={show ? 'text' : 'password'}
          autoComplete="new-password"
          placeholder="Type it again"
          error={errors[confirmName]}
          {...register(confirmName, {
            required: 'Confirm your password',
            validate: (v) => v === password || 'Passwords do not match',
          })}
        />
      </Field>
    </>
  );
}
